// frontend/src/pages/superAdmin/SchoolReport.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import api from '../../services/api';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';
import Swal from 'sweetalert2';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const SchoolReport = ({ user, setUser }) => {
  const { schoolId } = useParams();
  const [school, setSchool] = useState(null);
  const [fiscalYear, setFiscalYear] = useState(null);
  const [scores, setScores] = useState(null);
  const [iitDetails, setIitDetails] = useState(null);
  const [eitDetails, setEitDetails] = useState(null);
  const [oitDetails, setOitDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Get active fiscal year
        const fiscalYearResponse = await api.get('/fiscal-years/active');
        const activeFiscalYear = fiscalYearResponse.data;
        setFiscalYear(activeFiscalYear);
        
        // Get school details
        const schoolResponse = await api.get(`/schools/${schoolId}`);
        setSchool(schoolResponse.data);
        
        // Get total scores
        const scoresResponse = await api.get(`/reports/total/${schoolId}/${activeFiscalYear.id}`);
        setScores(scoresResponse.data);
        
        // Get IIT details
        const iitResponse = await api.get(`/reports/iit/${schoolId}/${activeFiscalYear.id}`);
        setIitDetails(iitResponse.data);
        
        // Get EIT details
        const eitResponse = await api.get(`/reports/eit/${schoolId}/${activeFiscalYear.id}`);
        setEitDetails(eitResponse.data);
        
        // Get OIT details
        const oitResponse = await api.get(`/reports/oit/${schoolId}/${activeFiscalYear.id}`);
        setOitDetails(oitResponse.data);
      } catch (error) {
        console.error('Error fetching report data:', error);
        Swal.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถดึงข้อมูลรายงานได้',
          confirmButtonText: 'ตกลง'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [schoolId]);

  // Prepare chart data
  const pieChartData = {
    labels: ['IIT', 'EIT1', 'EIT2', 'OIT'],
    datasets: [
      {
        data: scores ? [
          scores.iit_score, 
          scores.eit1_score, 
          scores.eit2_score, 
          scores.oit_score
        ] : [0, 0, 0, 0],
        backgroundColor: [
          'rgba(54, 162, 235, 0.6)',
          'rgba(255, 99, 132, 0.6)',
          'rgba(255, 206, 86, 0.6)',
          'rgba(75, 192, 192, 0.6)',
        ],
        borderColor: [
          'rgba(54, 162, 235, 1)',
          'rgba(255, 99, 132, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const barChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'คะแนนตามประเภทการประเมิน',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
      }
    }
  };

  const barChartData = {
    labels: ['คะแนนรวม', 'IIT (30)', 'EIT (30)', 'OIT (40)'],
    datasets: [
      {
        label: 'คะแนน',
        data: scores ? [
          scores.total_score,
          scores.iit_score,
          scores.eit_score,
          scores.oit_score
        ] : [0, 0, 0, 0],
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
      },
    ],
  };

  const getRatingClass = (rating) => {
    switch(rating) {
      case 'ผ่านดีเยี่ยม':
        return 'bg-success';
      case 'ผ่านดี':
        return 'bg-info';
      case 'ผ่าน':
        return 'bg-primary';
      case 'ต้องปรับปรุง':
        return 'bg-warning';
      case 'ต้องปรับปรุงโดยด่วน':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  };

  if (loading) {
    return (
      <div className="wrapper">
        <Header user={user} title="รายงานผลการประเมินรายโรงเรียน" />
        <Sidebar user={user} />
        <div className="content-wrapper">
          <div className="content-header">
            <div className="container-fluid">
              <div className="row mb-2">
                <div className="col-sm-6">
                  <h1 className="m-0">รายงานผลการประเมินรายโรงเรียน</h1>
                </div>
                <div className="col-sm-6">
                  <ol className="breadcrumb float-sm-right">
                    <li className="breadcrumb-item">
                      <Link to="/super-admin/dashboard">หน้าหลัก</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="/super-admin/reports">รายงาน</Link>
                    </li>
                    <li className="breadcrumb-item active">รายงานรายโรงเรียน</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <div className="content">
            <div className="container-fluid">
              <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">กำลังโหลด...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="wrapper">
      <Header user={user} title="รายงานผลการประเมินรายโรงเรียน" />
      <Sidebar user={user} />
      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">รายงานผลการประเมินรายโรงเรียน</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-right">
                  <li className="breadcrumb-item">
                    <Link to="/super-admin/dashboard">หน้าหลัก</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="/super-admin/reports">รายงาน</Link>
                  </li>
                  <li className="breadcrumb-item active">รายงานรายโรงเรียน</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        <div className="content">
          <div className="container-fluid">
            {school && fiscalYear && scores && (
              <>
                <div className="row">
                  <div className="col-lg-12">
                    <div className="card">
                      <div className="card-header">
                        <h3 className="card-title">
                          <i className="fas fa-chart-pie mr-1"></i>
                          ข้อมูลทั่วไป
                        </h3>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-6">
                            <p><strong>โรงเรียน:</strong> {school.name}</p>
                            <p><strong>รหัสโรงเรียน:</strong> {school.code}</p>
                            <p><strong>ผู้อำนวยการ:</strong> {school.director}</p>
                          </div>
                          <div className="col-md-6">
                            <p><strong>ปีงบประมาณ:</strong> {fiscalYear.year}</p>
                            <p>
                              <strong>ระดับผลการประเมิน:</strong>{' '}
                              <span className={`badge ${getRatingClass(scores.rating)}`}>
                                {scores.rating}
                              </span>
                            </p>
                            <p><strong>คะแนนรวม:</strong> {scores.total_score.toFixed(2)}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-8">
                    <div className="card">
                      <div className="card-header">
                        <h3 className="card-title">
                          <i className="fas fa-chart-bar mr-1"></i>
                          ผลการประเมินตามประเภท
                        </h3>
                      </div>
                      <div className="card-body">
                        <Bar options={barChartOptions} data={barChartData} />
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-4">
                    <div className="card">
                      <div className="card-header">
                        <h3 className="card-title">
                          <i className="fas fa-chart-pie mr-1"></i>
                          สัดส่วนคะแนน
                        </h3>
                      </div>
                      <div className="card-body">
                        <Pie data={pieChartData} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-12">
                    <div className="card">
                      <div className="card-header">
                        <h3 className="card-title">
                          <i className="fas fa-list mr-1"></i>
                          รายละเอียดคะแนน
                        </h3>
                      </div>
                      <div className="card-body">
                        <div className="table-responsive">
                          <table className="table table-bordered">
                            <thead>
                              <tr>
                                <th>ประเภทการประเมิน</th>
                                <th>คะแนนเต็ม</th>
                                <th>คะแนนที่ได้</th>
                                <th>ร้อยละ</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>IIT (บุคลากรภายใน)</td>
                                <td>30</td>
                                <td>{scores.iit_score.toFixed(2)}</td>
                                <td>{((scores.iit_score / 30) * 100).toFixed(2)}%</td>
                              </tr>
                              <tr>
                                <td>EIT1 (ผู้มีส่วนได้ส่วนเสียโรงเรียนกำหนด)</td>
                                <td>15</td>
                                <td>{scores.eit1_score.toFixed(2)}</td>
                                <td>{((scores.eit1_score / 15) * 100).toFixed(2)}%</td>
                              </tr>
                              <tr>
                                <td>EIT2 (ผู้มีส่วนได้ส่วนเสียทั่วไป)</td>
                                <td>15</td>
                                <td>{scores.eit2_score.toFixed(2)}</td>
                                <td>{((scores.eit2_score / 15) * 100).toFixed(2)}%</td>
                              </tr>
                              <tr>
                                <td>OIT (การเปิดเผยข้อมูล)</td>
                                <td>40</td>
                                <td>{scores.oit_score.toFixed(2)}</td>
                                <td>{((scores.oit_score / 40) * 100).toFixed(2)}%</td>
                              </tr>
                              <tr className="table-active">
                                <td><strong>รวม</strong></td>
                                <td><strong>100</strong></td>
                                <td><strong>{scores.total_score.toFixed(2)}</strong></td>
                                <td><strong>{scores.total_score.toFixed(2)}%</strong></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional tabs for detailed scores */}
                <div className="row">
                  <div className="col-lg-12">
                    <div className="card">
                      <div className="card-header p-2">
                        <ul className="nav nav-pills">
                          <li className="nav-item">
                            <a className="nav-link active" href="#iit-tab" data-bs-toggle="tab">
                              IIT
                            </a>
                          </li>
                          <li className="nav-item">
                            <a className="nav-link" href="#eit-tab" data-bs-toggle="tab">
                              EIT
                            </a>
                          </li>
                          <li className="nav-item">
                            <a className="nav-link" href="#oit-tab" data-bs-toggle="tab">
                              OIT
                            </a>
                          </li>
                        </ul>
                      </div>
                      <div className="card-body">
                        <div className="tab-content">
                          <div className="active tab-pane" id="iit-tab">
                            {iitDetails && (
                              <div className="table-responsive">
                                <table className="table table-striped">
                                  <thead>
                                    <tr>
                                      <th>คำถาม</th>
                                      <th>ค่าเฉลี่ย</th>
                                      <th>น้ำหนัก</th>
                                      <th>คะแนน</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {iitDetails.questions.map((q, index) => (
                                      <tr key={index}>
                                        <td>{q.question_text}</td>
                                        <td>{(Number(q.avg_score ?? 0)).toFixed(2)}</td>
                                        <td>{q.weight}</td>
                                        <td>{(q.avg_score * q.weight).toFixed(2)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                          <div className="tab-pane" id="eit-tab">
                            {eitDetails && (
                              <>
                                <h4>EIT1 (ผู้มีส่วนได้ส่วนเสียโรงเรียนกำหนด)</h4>
                                <div className="table-responsive mb-4">
                                  <table className="table table-striped">
                                    <thead>
                                      <tr>
                                        <th>คำถาม</th>
                                        <th>ค่าเฉลี่ย</th>
                                        <th>น้ำหนัก</th>
                                        <th>คะแนน</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {eitDetails.eit1.questions.map((q, index) => (
                                        <tr key={index}>
                                          <td>{q.question_text}</td>
                                          <td>{(Number(q.avg_score ?? 0)).toFixed(2)}</td>
                                          <td>{q.weight}</td>
                                          <td>{(q.avg_score * q.weight).toFixed(2)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>

                                <h4>EIT2 (ผู้มีส่วนได้ส่วนเสียทั่วไป)</h4>
                                <div className="table-responsive">
                                  <table className="table table-striped">
                                    <thead>
                                      <tr>
                                        <th>คำถาม</th>
                                        <th>ค่าเฉลี่ย</th>
                                        <th>น้ำหนัก</th>
                                        <th>คะแนน</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {eitDetails.eit2.questions.map((q, index) => (
                                        <tr key={index}>
                                          <td>{q.question_text}</td>
                                          <td>{(Number(q.avg_score ?? 0)).toFixed(2)}</td>
                                          <td>{q.weight}</td>
                                          <td>{(q.avg_score * q.weight).toFixed(2)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </>
                            )}
                          </div>
                          <div className="tab-pane" id="oit-tab">
                            {oitDetails && (
                              <div className="table-responsive">
                                <table className="table table-striped">
                                  <thead>
                                    <tr>
                                      <th>คำถาม</th>
                                      <th>ลิงก์</th>
                                      <th>คะแนนเฉลี่ย</th>
                                      <th>น้ำหนัก</th>
                                      <th>คะแนน</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {oitDetails.questions.map((q, index) => (
                                      <tr key={index}>
                                        <td>{q.question_text}</td>
                                        <td>
                                          <a href={q.link} target="_blank" rel="noopener noreferrer">
                                            {q.link}
                                          </a>
                                        </td>
                                        <td>{(Number(q.avg_score ?? 0)).toFixed(2)}</td>
                                        <td>{q.weight}</td>
                                        <td>{(q.avg_score * q.weight).toFixed(2)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default SchoolReport;